import dayjs from 'dayjs';
import { memo, useMemo, useState, useEffect } from 'react';
import { generatePath } from 'react-router-dom';
import { validate as validateUUID } from 'uuid';

import { Paths } from '../../../app/constants/paths';
import { useTenant } from '../../../app/context/tenant-context';
import { useUserId } from '../../../app/hooks/accounts';
import { useSaveView } from '../../../app/hooks/view';
import type { TabType } from '../../context/tab-manager-context';
import { constructPersistedView } from '../../utils/view-state-helpers';
import { ExportModalV2, ExportModalSnapshotOnly } from '../export-modal/export-modal';

import { Modal, ModalHeader, ModalBody, ModalFooter } from '@controlrooms/components';
import { ViewState, ViewShareType } from '@controlrooms/models';

interface ShareViewLinkModalProps {
  viewSnapshot: ViewState;
  tabType: TabType;
  onClose: () => void;
  createNewForShare?: boolean;
}

export const ShareViewLinkModal = memo(
  ({ viewSnapshot, tabType, onClose, createNewForShare = false }: ShareViewLinkModalProps) => {
    const currentUserId = useUserId();
    const { mutateAsync: createView, isLoading } = useSaveView();
    const { tenant } = useTenant();
    const isViewType = tabType === 'VIEWS';

    // For both VIEWS and other types: need to create view if unsaved (tab id / UUID) to get shareable hash
    const isNewView =
      createNewForShare ||
      viewSnapshot.view_id === '' ||
      viewSnapshot.viewId === 'default' ||
      (viewSnapshot.view_id !== '' && validateUUID(viewSnapshot.view_id));

    const persistView = useMemo(
      () =>
        constructPersistedView({
          viewState: { ...viewSnapshot, tabType: viewSnapshot.tabType || tabType },
          viewName: '',
          currentUserId,
          viewDescription: '',
          shareType: ViewShareType.PRIVATE,
          shared: false,
        }),
      [viewSnapshot, tabType, currentUserId],
    );

    const [viewHash, setViewHash] = useState(isNewView ? '' : viewSnapshot.view_id);
    const [viewhashError, setViewhashError] = useState('');

    useEffect(() => {
      const initializeViewHash = async () => {
        if (isNewView) {
          try {
            persistView.view.name = `Anonymous_${dayjs().valueOf()}`;
            persistView.view_name = `Anonymous_${dayjs().valueOf()}`;
            persistView.view.description = '';
            persistView.default_view = false;
            persistView.share_type = ViewShareType.ANONYMOUS;
            const hash = await createView(persistView);
            setViewHash(hash);
          } catch (error) {
            setViewhashError('Failed to create a view hash.');
          }
        } else {
          setViewHash(viewSnapshot.view_id);
        }
      };
      initializeViewHash();
    }, [isNewView, createView, persistView, viewSnapshot.view_id]);

    const viewLink = useMemo(
      () =>
        viewHash
          ? `${window.location.origin}${generatePath(Paths.SHARE, {
              tenantId: tenant.toString(),
              hash: viewHash,
            })}`
          : '',
      [tenant, viewHash],
    );

    const modalContent = isViewType ? (
      <ExportModalV2
        view={viewSnapshot}
        link={viewLink}
        viewHash={viewHash}
        isError={!!viewhashError}
        isLoading={isLoading}
      />
    ) : (
      <ExportModalSnapshotOnly
        link={viewLink}
        isError={!!viewhashError}
        isLoading={isLoading && !viewLink}
      />
    );

    return (
      <Modal open={true} onCancel={onClose} size="md">
        <ModalHeader title={isViewType ? 'Share View' : 'Share'} />
        <ModalBody>{modalContent}</ModalBody>
        <ModalFooter
          confirmText="Close"
          onConfirm={onClose}
          cancelTestId="modal-close-icon-delete-alert"
        />
      </Modal>
    );
  },
  (prevProps, nextProps) =>
    prevProps.tabType === nextProps.tabType &&
    prevProps.createNewForShare === nextProps.createNewForShare &&
    JSON.stringify(prevProps.viewSnapshot) === JSON.stringify(nextProps.viewSnapshot),
);
